import { PropsWithChildren, ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system/text-button';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

export interface OrderTutorialContext {
  onClickPositionModes: () => void;
}

export interface OrderTutorialItem {
  imageUrl: string;
  title: ReactNode;
  englishTitle: string;
  content: (ctx: OrderTutorialContext) => ReactNode;
}

const ConnectedUl = ({ children }: PropsWithChildren) => {
  return (
    <ul
      className={vstack({
        gap: 12,
        alignItems: 'flex-start',
        '& li': {
          position: 'relative',
          listStyle: 'disc',
          ml: 20,
          pl: 4,
        },
        '& > li > span': {
          textStyle: 'Text/L/Regular',
          color: 'content.default.level-5',
        },
        '& > li:not(:last-child) > span': {
          _before: {
            content: '""',
            position: 'absolute',
            borderLeftWidth: '1px',
            borderLeftStyle: 'solid',
            borderColor: 'border.default.level-3',
            left: -12,
            top: 14,
            height: 34,
            zIndex: 0,
          },
        },
      })}
    >
      {children}
    </ul>
  );
};

const BaseDl = ({ children }: PropsWithChildren) => {
  return (
    <dl
      className={css({
        '& dt': {
          textStyle: 'Text/L/Strong',
          color: 'content.default.level-5',
        },
        '& dd': {
          textStyle: 'Text/L/Regular',
          color: 'content.default.level-3',
        },
        '& dd + dt': {
          mt: 12,
        },
      })}
    >
      {children}
    </dl>
  );
};

export const ExistingUserTutorialItems: OrderTutorialItem[] = [
  {
    imageUrl: '/images/web/tutorial/v3/onboarding_beginner_2_new.png',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_your_order_type' />,
    englishTitle: 'Select your order type',
    content: () => (
      <BaseDl>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Market_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_immediately_at_the_current_market_price' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Limit_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_at_a_specified_price_or_better' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Trigger_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_a_market_order_when_the_price_reaches_a_specified_number' />
        </dd>
      </BaseDl>
    ),
  },
];

export const NewUserTutorialItems: OrderTutorialItem[] = [
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_1.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_your_trading_mode' />,
    englishTitle: 'Select your trading mode',
    content: (ctx) => (
      <ConnectedUl>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Click_Trade_settings' />
          </span>
        </li>
        <li>
          <span>
            <FormattedMessage id='Common.r260130.Click' />{' '}
            <TextButton
              size='large'
              label={<FormattedMessage id='Autogen_common.r251126.s_Position_modes' />}
              onClick={() => {
                segment.track(TradeEvent.TradingOnboardingLinkClick, {
                  title: 'Select your trading mode',
                  onboarding_page_num: 1,
                  link_text: 'Position modes',
                });
                ctx.onClickPositionModes();
              }}
            />
          </span>
        </li>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Select_the_mode_to_optimize_your_trading_approach' />
          </span>
        </li>
      </ConnectedUl>
    ),
  },
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_2.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_your_order_type' />,
    englishTitle: 'Select your order type',
    content: () => (
      <BaseDl>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Market_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_immediately_at_the_current_market_price' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Limit_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_at_a_specified_price_or_better' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Trigger_Order' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Executes_a_market_order_when_the_price_reaches_a_specified_number' />
        </dd>
      </BaseDl>
    ),
  },
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_3.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_your_order_input_method' />,
    englishTitle: 'Select your order input method',
    content: () => (
      <BaseDl>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Order_by_position_size' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Set_the_order_using_notional_USDT_value_or_token_quantity' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Order_by_margin' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_Specify_the_margin_notional_value_is_calculated_as_Margin_Leverage' />
        </dd>
      </BaseDl>
    ),
  },
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_4.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_your_margin_mode' />,
    englishTitle: 'Select your margin mode',
    content: () => (
      <BaseDl>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Isolated_margin' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_A_separate_isolated_margin_account_is_assigned_to_each_trading_pair' />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Cross_margin' />
        </dt>
        <dd>
          <FormattedMessage id='Autogen_common.r251126.s_All_positions_in_a_cross_margin_account_use_the_same_collateral' />
        </dd>
      </BaseDl>
    ),
  },
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_5.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Select_leverage' />,
    englishTitle: 'Select leverage',
    content: () => (
      <ConnectedUl>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Adjust_the_leverage_slider_or_enter_the_desired_leverage' />
          </span>
        </li>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Leverage_increases_position_size_without_needing_to_increase_your_margin' />
          </span>
        </li>
      </ConnectedUl>
    ),
  },
  {
    imageUrl: '/images/web/tutorial/v5/onboarding_beginner_6.webp',
    title: <FormattedMessage id='Autogen_common.r251126.s_Set_Stop_Loss_manage_risk' />,
    englishTitle: 'Set Stop Loss & manage risk',
    content: () => (
      <ConnectedUl>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Click_the_box_next_to_TP_SL_Take_Profit_Stop_Loss' />
          </span>
        </li>
        <li>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Enter_a_take_profit_or_stop_loss_price' />
          </span>
        </li>
      </ConnectedUl>
    ),
  },
];
